// The top bar's location line (docs/design.md "Location line";
// docs/superpowers/specs/2026-11-03-navigation-design.md, section 2): where
// a page is, as owner / repo / subject, each part a link up, the last the
// page itself. Worked out from the route alone, with what only the page
// knows (a pull request's title, a history it resolved, a compare's base
// as read) handed in as `Here`.
import { VIEW_PARAM, locatorPath, prPath, repoIndexPath, repoPath, subjectPath, type PrTab, type Route, type SubjectView } from '../router';
import { subjectKey, type Subject } from '../subject';
import { carryFilter } from './filefilter';

export interface LocationPart {
  /** Its words. */
  label: string;
  /** Said first, apart from the words: a pull request's number. */
  lead?: string;
  /** Where it leads: up for every part but the last, which is the page itself. */
  href: string;
  /** What sets it apart from the part before (unused on the first). */
  sep: '/' | '·';
}

export interface Location {
  parts: LocationPart[];
  /** The subject's key (`subjectKey`), to copy from the last part: what an agent takes in `changeling_connect`. Null where the page has no subject. */
  key: string | null;
  /**
   * What the last part is marked (`aria-current`): the current `location`
   * where the page's own tabs mark the current page (a pull request's, a
   * compare's or a history's views, the repository page's tabs, the
   * index's views), so one link only is the current page; else the `page`.
   */
  current: 'page' | 'location';
}

/** What the page knows of its subject beyond its route. */
export interface Here {
  subject: Subject;
  /** A pull request's title, once read. */
  title?: string | null;
  /** A compare's base as its page read it, when its key names none (the default branch). */
  base?: string | null;
}

const isSha = (ref: string) => /^[0-9a-f]{40}$/i.test(ref);
/** A ref as a sentence names it: a commit by its short SHA, anything else whole. */
const short = (ref: string) => (isSha(ref) ? ref.slice(0, 7) : ref);

/** owner / repo, each linking up: the owner to the repository index's owner view, the repository to its page. */
function repoParts(owner: string, repo: string): LocationPart[] {
  return [
    { label: owner, href: repoIndexPath({ owner }), sep: '/' },
    { label: repo, href: repoPath(owner, repo), sep: '/' },
  ];
}

/** The view `route` shows of a subject of `kind`, if it shows one. */
function viewOf(route: Route, kind: Subject['kind']): SubjectView | undefined {
  if (route.name === 'compare' || route.name === 'ref') return kind === route.name ? route.view : undefined;
  return 'subject' in route && kind === 'pull' ? (route.name as PrTab) : undefined;
}

/** The subject's own part, the page itself on the view shown: `#12 title`, `main ← feat/x` (a fork's head `ana:feat`), `main · src`. */
function subjectPart(here: Here, view: SubjectView | undefined, query: URLSearchParams): LocationPart {
  const s = here.subject;
  const href = carryFilter(subjectPath<Subject>(s, view), query);
  switch (s.kind) {
    case 'pull':
      return { lead: `#${s.number}`, label: here.title ?? '', href, sep: '/' };
    case 'compare': {
      const head = s.headRepo ? `${s.headRepo.owner}:${s.headRepo.repo === null ? '' : s.headRepo.repo + ':'}${short(s.head)}` : short(s.head);
      return { label: `${short(here.base ?? s.base ?? 'default branch')} ← ${head}`, href, sep: '/' };
    }
    case 'ref':
      return { label: `${short(s.ref)}${s.path === null ? '' : ' · ' + s.path}`, href, sep: '/' };
  }
}

/**
 * Where the page at `route` (with `query`) is, or null for a page outside
 * any repository and the index (the dashboard, settings, feedback). `here`
 * is the page's subject as it knows it, which wins over the route's.
 */
export function locationOf(route: Route, query: URLSearchParams, here: Here | null = null): Location | null {
  if (here) return { parts: [...repoParts(here.subject.owner, here.subject.repo), subjectPart(here, viewOf(route, here.subject.kind), query)], key: subjectKey(here.subject), current: 'location' };
  switch (route.name) {
    case 'repos': {
      const owner = query.get('owner') || null;
      const view = query.get(VIEW_PARAM) === 'branches' ? 'branches' : null;
      const self = repoIndexPath({ owner, view });
      if (!owner) return { parts: [{ label: 'Repositories', href: self, sep: '/' }], key: null, current: 'location' };
      return { parts: [{ label: 'Repositories', href: repoIndexPath(), sep: '/' }, { label: owner, href: self, sep: '·' }], key: null, current: 'location' };
    }
    case 'repo':
      return {
        parts: [...repoParts(route.owner, route.repo), { label: route.tab === 'branches' ? 'Branches' : 'Pull requests', href: repoPath(route.owner, route.repo, route.tab), sep: '/' }],
        key: null,
        current: 'location',
      };
    case 'ref':
      // Before its page resolves it: as its URL names it, with no key (a locator has none) and no tabs yet.
      return {
        parts: [...repoParts(route.locator.owner, route.locator.repo), { label: `${route.locator.spec}${route.locator.path === undefined ? '' : ' · ' + route.locator.path}`, href: locatorPath(route.locator, route.view), sep: '/' }],
        key: null,
        current: 'page',
      };
    default:
      return 'subject' in route ? locationOf(route, query, { subject: route.subject }) : null;
  }
}
